import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Autocomplete,
  Button,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import { removeSampleTestArray } from "../../actions/cleanup";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { retailQuickSaleCategoriesTooltip } from "../../../constants/TooltipInformation";
import { fieldValidationHelperText } from "../../../constants/validationHelperText";
import { RetailPropertyEnum } from "../../../constants/NavbarList";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const RetailQuickSaleCategory = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [quickSaleCategory, setQuickSaleCategory] = useState<string | null>(
    null,
  );
  const [outletName, setOutletName] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [nameError, setNameError] = useState<boolean>(false);
  const [outletNameError, setOutletNameError] = useState<boolean>(false);
  const [retailQuickSaleCategories, setRetailQuickSaleCategories] = useState<
    any[]
  >([]);
  const [outletList, setOutletList] = useState<any>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const payload = {
    "Quick Sale Category": quickSaleCategory,
    "Outlet Name": outletName,
    Active: active ? "Yes" : "No",
  };

  const validateInput = () => {
    setNameError(false);
    setOutletNameError(false);
    if (!quickSaleCategory || String(quickSaleCategory).trim() === "") {
      setNameError(true);
      return false;
    }
    if (
      !validateString(
        quickSaleCategory,
        validationLength.quickSaleCategory,
        validationType.NAME,
      )
    ) {
      setNameError(true);
      return false;
    }
    if (!outletName || String(outletName).trim() === "") {
      setOutletNameError(true);
      return false;
    }
    if (
      !validateString(
        outletName,
        validationLength.outletName,
        validationType.SPACE,
      )
    ) {
      setOutletNameError(true);
      return false;
    }
    return true;
  };

  const editRetailQuickSaleCategories = (list: any) => {
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setQuickSaleCategory(null);
      setOutletName(null);
      setActive(true);
      setNameError(false);
      setOutletNameError(false);
      return;
    }
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setQuickSaleCategory(list?.["Quick Sale Category"] ?? null);
    setOutletName(list?.["Outlet Name"] ?? null);
    setActive(list?.["Active"] === "Yes");
    setNameError(false);
    setOutletNameError(false);
  };

  const updateRetailQuickSaleCategories = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        retailQuickSaleCategories.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.QUICKSALECATEGORY,
        quickSaleCategory,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setNameError(false);
    setOutletNameError(false);
    setRetailQuickSaleCategories((prev: any[]) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editRetailQuickSaleCategories(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${RetailPropertyEnum.quickSaleCategories} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addRetailQuickSaleCategories = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        retailQuickSaleCategories,
        DNDFieldsEnum.QUICKSALECATEGORY,
        quickSaleCategory,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRetailQuickSaleCategories((prev: any[]) =>
      updateData(
        prev !== null && prev?.length
          ? [
              ...prev,
              {
                id: retailQuickSaleCategories.length + 1,
                ...payload,
              },
            ]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editRetailQuickSaleCategories(null);
    setEditClicked(false);
  };

  const deleteRetailQuickSaleCategories = (id: number) => {
    setRetailQuickSaleCategories(deleteItem(retailQuickSaleCategories, id));
  };

  useEffect(() => {
    if (fieldValues) {
      setRetailQuickSaleCategories(
        fieldValues[RetailPropertyEnum.quickSaleCategories],
      );
      setOutletList(
        fillAutocompleteValues(
          fieldValues[RetailPropertyEnum.outletSetup],
          DNDFieldsEnum.OUTLETNAME,
          "",
        ),
      );
    }
  }, []);

  useEffect(() => {
    data(retailQuickSaleCategories);
    if (editClicked) {
      setSelectedId(
        checkData(retailQuickSaleCategories, selectedId) ? selectedId : null,
      );
    }
  }, [retailQuickSaleCategories]);

  const quickSaleHelperText = nameError
    ? !quickSaleCategory || String(quickSaleCategory).trim() === ""
      ? validationMessages.fieldRequired
      : validationMessages.retailQuickSaleCategoryName
    : fieldValidationHelperText.retailQuickSaleCategory;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Quick Sale Categories
              <Tooltip
                title={tooltipComponent(
                  retailQuickSaleCategoriesTooltip.header,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => editRetailQuickSaleCategories(null)}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editRetailQuickSaleCategories(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Quick Sale Category
              <Tooltip
                title={tooltipComponent(
                  retailQuickSaleCategoriesTooltip.quickSaleCategory,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailQuickSaleCategory"
              variant="outlined"
              placeholder="Quick Sale Category"
              value={quickSaleCategory ?? ""}
              helperText={quickSaleHelperText}
              error={nameError}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setQuickSaleCategory(e.target.value || null);
                setNameError(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Outlet Name
              <Tooltip
                title={tooltipComponent(
                  retailQuickSaleCategoriesTooltip.outletName,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Stack direction="row" width="100%" gap="5px">
              <Autocomplete
                id="retailQuickSaleOutletName"
                disablePortal
                options={removeSampleTestArray(outletList ?? [])}
                sx={{ width: "100%" }}
                value={outletName}
                disabled={!outletList}
                onChange={(_e, newValue) => {
                  setOutletName(newValue ?? null);
                  setOutletNameError(false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Outlet Name"
                    variant="outlined"
                    placeholder="Outlet Name"
                    error={outletNameError}
                    helperText={
                      outletNameError
                        ? !outletName || String(outletName).trim() === ""
                          ? validationMessages.fieldRequired
                          : validationMessages.outletName
                        : ""
                    }
                  />
                )}
              />
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Active?
              <Tooltip
                title={tooltipComponent(
                  retailQuickSaleCategoriesTooltip.active,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label=""
            />
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={
                selectedId
                  ? updateRetailQuickSaleCategories
                  : addRetailQuickSaleCategories
              }
              id="addRetailQuickSaleCategory"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Quick Sale
              Category
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">
              Quick Sale Categories Available
            </Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {retailQuickSaleCategories?.length ? (
                <DraggableItemList
                  itemData={retailQuickSaleCategories}
                  onOrderUpdate={(order) => setRetailQuickSaleCategories(order)}
                  onEdit={(info) => {
                    editRetailQuickSaleCategories(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteRetailQuickSaleCategories(id)}
                  code={DNDFieldsEnum.QUICKSALECATEGORY}
                  name={DNDFieldsEnum.OUTLETNAME}
                />
              ) : (
                <Typography variant="body2">
                  No Quick Sale Categories Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RetailQuickSaleCategory;
